import { isSedrLibraryPath } from '../../app/navigationState';
import { SedrListPage } from './SedrListPage';
import { mountSedrLibrary } from './SedrLibraryPage';
import { mountSedrRoom } from './SedrRoomPage';

function getSedrRoomId(): string | null {
  const match = window.location.pathname.match(/^\/sedr\/([^/]+)/);
  const id = match?.[1] ?? null;
  return id && id !== 'library' ? id : null;
}

function prefersSedrMotion(): boolean {
  return (
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && !window.matchMedia('(max-width: 768px)').matches
  );
}

type MountedPane =
  | { kind: 'none' }
  | { kind: 'room'; roomId: string }
  | { kind: 'library' };

export function SedrModulePage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'sedr-module';

  const workspace = document.createElement('div');
  workspace.className = 'sedr-workspace sedr-browse-layout';
  workspace.setAttribute('data-sedr-workspace', '');

  const gutter = document.createElement('div');
  gutter.className = 'sedr-workspace__gutter';
  gutter.setAttribute('aria-hidden', 'true');

  const listPane = document.createElement('div');
  listPane.className = 'sedr-workspace__list';
  const listHandle = SedrListPage();
  listPane.append(listHandle.element);

  const roomPane = document.createElement('div');
  roomPane.className = 'sedr-workspace__room';

  workspace.append(gutter, listPane, roomPane);
  page.append(workspace);

  let mounted: MountedPane = { kind: 'none' };
  let lastDetailPath = window.location.pathname;
  let disposePane: (() => void) | null = null;
  let allowMotion = false;

  const afterCloseMotion = (done: () => void) => {
    const animate = allowMotion && prefersSedrMotion();
    if (!animate) {
      done();
      return;
    }
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      workspace.removeEventListener('transitionend', onTransitionEnd);
      done();
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== workspace || event.propertyName !== 'grid-template-columns') return;
      finish();
    };
    workspace.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(finish, 1100);
  };

  const clearPane = () => {
    disposePane?.();
    disposePane = null;
    mounted = { kind: 'none' };
    roomPane.replaceChildren();
  };

  const syncRoute = () => {
    const onLibrary = isSedrLibraryPath();
    const roomId = getSedrRoomId();
    const open = onLibrary || Boolean(roomId);
    const animate = allowMotion && prefersSedrMotion();
    const closing = mounted.kind !== 'none' && !open;

    workspace.classList.toggle('sedr-workspace--instant', !animate);
    workspace.classList.toggle('sedr-workspace--closing', animate && closing);
    workspace.classList.toggle('sedr-workspace--open', open);

    if (!animate) {
      workspace.classList.toggle('sedr-workspace--ready', open);
    }

    if (onLibrary && mounted.kind === 'library' && !closing) {
      const currentPath = window.location.pathname;
      if (currentPath !== lastDetailPath) {
        lastDetailPath = currentPath;
        roomPane.dispatchEvent(new Event('sedr:library-route'));
      }
      return;
    }

    if (roomId && mounted.kind === 'room' && mounted.roomId === roomId && !closing) {
      const currentPath = window.location.pathname;
      if (currentPath !== lastDetailPath) {
        lastDetailPath = currentPath;
        roomPane.dispatchEvent(new Event('sedr:room-nav'));
      }
      return;
    }

    lastDetailPath = window.location.pathname;

    if (!open) {
      const shouldReloadList = mounted.kind !== 'none';
      if (shouldReloadList) {
        void listHandle.reload();
      }
      workspace.classList.remove('sedr-workspace--ready');
      afterCloseMotion(() => {
        if (isSedrLibraryPath() || getSedrRoomId()) return;
        clearPane();
        workspace.classList.remove('sedr-workspace--closing');
      });
      return;
    }

    workspace.classList.remove('sedr-workspace--closing');

    if (onLibrary) {
      if (mounted.kind !== 'library') {
        clearPane();
        disposePane = mountSedrLibrary(roomPane);
        mounted = { kind: 'library' };
      }
    } else if (roomId) {
      if (mounted.kind !== 'room' || mounted.roomId !== roomId) {
        clearPane();
        disposePane = mountSedrRoom(roomPane, roomId);
        mounted = { kind: 'room', roomId };
      }
    }

    if (animate) {
      requestAnimationFrame(() => {
        workspace.classList.add('sedr-workspace--ready');
      });
    }
  };

  workspace.addEventListener('sedr:route', () => syncRoute());
  syncRoute();

  requestAnimationFrame(() => {
    allowMotion = true;
  });

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      disposePane?.();
      disposePane = null;
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
